import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { SetupAppearanceSelector } from '@traceio-org/ui';
import { usePopupDismiss, type AppearancePreference } from '@traceio-org/auth-ui';
import { ACCOUNT_TRIGGER_SELECTOR } from './AccountMenu';

/**
 * Where the menu opens from and gives focus back to: the sidebar's account row (ACCOUNT_TRIGGER_SELECTOR), whose
 * account menu holds the Appearance entry. The sidebar exists twice on a phone (the rail and the slide-out, which
 * closes on a choice): the visible row first, else the shell's Open navigation button, so focus never falls to the page.
 */
function appearanceTrigger(): HTMLElement | null {
  const rows = Array.from(document.querySelectorAll<HTMLElement>(ACCOUNT_TRIGGER_SELECTOR));
  const menuButton = document.querySelector<HTMLElement>('button.trace-mobile-trigger');
  const candidates = menuButton ? [...rows, menuButton] : rows;
  return candidates.find((element) => element.getClientRects().length > 0) ?? candidates[0] ?? null;
}

const GAP = 6;
const EDGE = 12;

/**
 * The appearance menu: the three SetupAppearanceSelector choices in trace-ui's workspace-menu surface, opened from the
 * account menu's Appearance entry. A non-modal dialog: focus moves to the checked choice, arrow keys move between the
 * choices (native radios, each applies at once so the page previews it), and Escape, a click outside or Tab out of it
 * close it and give focus back to the account row. Portaled into a trace-nav-portal element, as trace-ui's own menus,
 * so it reads the shell's surface tokens and sits above the page.
 */
export function AppearanceMenu({
  preference,
  shared,
  onChange,
  onClose,
}: {
  preference: AppearancePreference;
  /** True when the choice is stored on the parent domain the account hub shares (useAppearance `shared`). */
  shared: boolean;
  onChange: (next: AppearancePreference) => void;
  onClose: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  // Starts at the fallback place (never hidden: the focus move into it needs it visible) and is moved next to the
  // row before the first paint.
  const [style, setStyle] = useState<CSSProperties>({ position: 'fixed', left: EDGE, bottom: EDGE });

  // Above the account row, left-aligned with it, kept on screen; bottom-left when the row is not visible (a phone's
  // slide-out closes on a choice).
  useLayoutEffect(() => {
    const place = () => {
      const row = appearanceTrigger();
      const width = panel.current?.offsetWidth ?? 280;
      if (!row || !row.matches(ACCOUNT_TRIGGER_SELECTOR) || row.getClientRects().length === 0) {
        setStyle({ position: 'fixed', left: EDGE, bottom: EDGE });
        return;
      }
      const rect = row.getBoundingClientRect();
      const left = Math.max(EDGE, Math.min(rect.left, window.innerWidth - width - EDGE));
      setStyle({ position: 'fixed', left, bottom: Math.max(EDGE, window.innerHeight - rect.top + GAP) });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, []);

  usePopupDismiss({ panel, trigger: appearanceTrigger, onDismiss: onClose });

  return createPortal(
    <div className="trace-nav-portal">
      <div
        ref={panel}
        className="trace-workspace-menu admin-appearance-menu"
        role="dialog"
        aria-label="Appearance"
        style={style}
      >
        {/* trace-ui's SetupAppearance is the same three words as AppearancePreference. */}
        <SetupAppearanceSelector value={preference} onChange={onChange} />
        <p className="admin-appearance-menu__hint">
          System follows your device.
          {/* Only true when the cookie is on the shared parent domain, never for a host-only fallback. */}
          {shared ? ' The account hub uses the same choice.' : null}
        </p>
      </div>
    </div>,
    document.body,
  );
}
